import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { Line } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';
import { Widget } from 'src/dashboard/Componets/Widget';
import {
  UsageTypeSwitch,
  UsageType,
  UsageTypeValue,
} from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import { GraphsColorLegend } from 'src/common/components/GraphColorsLegend/GraphColorsLegend';
import { ChartType } from 'src/common/components/ChartTypeToggle/ChartTypeToggle';
import { AreaChart } from 'src/common/components/Graphs/common/Graphs/AreaChart';
import { parseResponseDateAndTime } from 'common/utils/parseResponseDate';
import { WidgetDailyUsageIndicator } from 'src/dashboard/Componets/WidgetDailyUpdateIndicator';
import { EmptyLineOrBarChart } from 'src/common/components/Graphs/common/Graphs/EmptyLineOrBarChart';
import { useProductsSummaryUsageData } from './hooks/useProductsSummaryUsageData';
import { AreaChartErrorBoundary } from 'src/dashboard/Componets/AreaErrorBoundary';
import { UsageWidget, UsageWidgetLegend } from 'src/dashboard/Componets/UsageWidget';
import { usageTooltipValueFormatter } from 'src/common/utils/mapUsageDTOtoAreaChartData';

const StyledChartContainer = styled(Box)(() => ({
  height: '100%',
}));

const usageChartTooltipLabelOrder = {
  Total: 1,
  Upload: 2,
  Download: 3,
  MO: 2,
  MT: 3,
};

type UsageChartWrapperProps = {
  productCode: string | null;
};

type UsageChartWidgetProps = {
  productCode: string;
};

type ProductSummaryUsageChartProps = {
  productCode: string;
  usageType: UsageTypeValue;
};

const ProductSummaryUsageChart = ({ productCode, usageType }: ProductSummaryUsageChartProps) => {
  const productUsageData = useProductsSummaryUsageData({
    productCode,
    chartType: ChartType.Cumulative,
    usageType,
  });

  return (
    <>
      <UsageWidgetLegend>
        <GraphsColorLegend legendItems={productUsageData.legend} />
        <WidgetDailyUsageIndicator date={parseResponseDateAndTime(productUsageData?.lastUpdated)} />
      </UsageWidgetLegend>
      <AreaChart
        datasets={productUsageData.datasets}
        limit={productUsageData.limit}
        yAxisLabel={productUsageData.yAxisLabel}
        xAxisLabels={productUsageData.xAxisLabels}
        realDataEndIndex={productUsageData.realDataEndIndex}
        tooltipValueFormatter={(value: number, unit?: string) =>
          usageTooltipValueFormatter(value, usageType, unit)
        }
        tooltipLabelPriority={usageChartTooltipLabelOrder}
      />
    </>
  );
};

export const UsageChartWidgetContent = ({ productCode }: UsageChartWidgetProps) => {
  const { t } = useTranslation();

  const [usageType, setUsageType] = useState<UsageTypeValue>(UsageType.Data);

  return (
    <UsageWidget
      title={t('dashboard.cumulativeUsageChart.title')}
      headerContent={
        <Box sx={{ marginRight: 'auto' }}>
          <UsageTypeSwitch value={usageType} onChange={setUsageType} />
        </Box>
      }
    >
      <StyledChartContainer role="figure" aria-label="Usage">
        <AreaChartErrorBoundary key={`${productCode}  ${usageType}`}>
          <ProductSummaryUsageChart usageType={usageType} productCode={productCode} />
        </AreaChartErrorBoundary>
      </StyledChartContainer>
    </UsageWidget>
  );
};

export const ProductSummaryUsageChartWidget = ({ productCode }: UsageChartWrapperProps) => {
  const { t } = useTranslation();

  if (!productCode) {
    return (
      <Widget title={t('dashboard.cumulativeUsageChart.title')}>
        <StyledChartContainer>
          <EmptyLineOrBarChart
            as={Line}
            emptyChartText={t('dashboard.placeholders.selectProduct')}
          />
        </StyledChartContainer>
      </Widget>
    );
  }

  return <UsageChartWidgetContent productCode={productCode} />;
};
